import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import 'moment/locale/pt-br';

export const formatarData = (data: string) => {
  moment.locale('pt-br');
  return moment(data, 'DD/MM').format('DD/MMM');
};

interface ChartConfigParams {
  isDark: boolean;
  chartColors: {
    primary: string;
    secondary: string;
    text: string;
    grid: string;
  };
}

export const getBaseChartConfig = ({ isDark, chartColors }: ChartConfigParams): Partial<ApexOptions> => ({
  legend: { show: false },
  chart: {
    toolbar: { show: false },
    background: 'transparent',
  },
  theme: {
    mode: isDark ? 'dark' : 'light',
  },
  grid: { borderColor: chartColors.grid },
  tooltip: {
    enabled: true,
    theme: isDark ? 'dark' : 'light',
  },
});

export const getLineChartConfig = (
  params: ChartConfigParams,
  color: string,
  categories: string[],
  hasData: boolean = true
): ApexOptions => ({
  ...getBaseChartConfig(params),
  colors: [color],
  stroke: { curve: 'smooth', width: 2 },
  xaxis: {
    categories,
    labels: { style: { colors: params.chartColors.text } }
  },
  yaxis: {
    labels: {
      style: { colors: params.chartColors.text },
      formatter: function(val) {
        return Math.floor(val).toString();
      }
    }
  },
  tooltip: {
    enabled: true,
    theme: params.isDark ? 'dark' : 'light',
    x: { show: false },
    y: {
      title: {
        formatter: function () {
          return '';
        }
      }
    }
  },
  ...((!hasData) && {
    annotations: {
      points: [{
        x: categories[0] || '',
        y: 0,
        marker: {
          size: 0
        },
        label: {
          borderColor: 'transparent',
          offsetY: 0,
          style: {
            color: params.chartColors.text,
            background: 'transparent',
            fontSize: '14px'
          },
          text: 'Nenhum dado disponível para o período selecionado'
        }
      }]
    }
  })
});

export const getBarChartConfig = (
  params: ChartConfigParams,
  color: string,
  categories: string[],
  hasData: boolean = true
): ApexOptions => ({
  ...getBaseChartConfig(params),
  colors: [color],
  plotOptions: {
    bar: {
      distributed: false,
    }
  },
  stroke: { curve: 'smooth', width: 0 },
  xaxis: {
    categories,
    labels: { style: { colors: params.chartColors.text } }
  },
  yaxis: {
    min: 0,
    tickAmount: 5,
    labels: {
      style: { colors: params.chartColors.text },
      formatter: function(val) {
        return Math.round(val).toString();
      }
    }
  },
  tooltip: {
    enabled: false,
    theme: params.isDark ? 'dark' : 'light'
  },
  ...((!hasData) && {
    annotations: {
      points: [{
        x: categories[0] || '',
        y: 0,
        marker: {
          size: 0
        },
        label: {
          borderColor: 'transparent',
          offsetY: 0,
          style: {
            color: params.chartColors.text,
            background: 'transparent',
            fontSize: '14px'
          },
          text: 'Nenhum dado disponível para o período selecionado'
        }
      }]
    }
  })
});

export const getHorizontalBarChartConfig = (
  params: ChartConfigParams,
  color: string,
  hasData: boolean = true
): ApexOptions => ({
  ...getBaseChartConfig(params),
  colors: [color],
  plotOptions: {
    bar: {
      horizontal: true,
      distributed: false,
      borderRadius: 8,
      dataLabels: {
        position: 'center'
      }
    }
  },
  dataLabels: {
    enabled: true,
    style: {
      colors: ['#fff']
    }
  },
  xaxis: {
    min: 0,
    tickAmount: 5,
    labels: {
      style: { colors: params.chartColors.text },
      formatter: function(val) {
        return Math.round(Number(val)).toString();
      }
    }
  },
  yaxis: {
    labels: {
      style: { colors: params.chartColors.text }
    }
  },
  tooltip: {
    enabled: false,
    theme: params.isDark ? 'dark' : 'light',
    x: { show: false },
    y: {
      title: {
        formatter: function () {
          return '';
        }
      }
    }
  },
  ...((!hasData) && {
    annotations: {
      xaxis: [{
        x: 0,
        x2: 0,
        strokeDashArray: 0,
        borderColor: 'transparent',
        label: {
          borderColor: 'transparent',
          offsetX: 200,
          style: {
            color: params.chartColors.text,
            background: 'transparent',
            fontSize: '14px'
          },
          text: 'Nenhum dado disponível para o período selecionado'
        }
      }]
    }
  })
});
